import React, { useEffect, useState } from "react";
import { api, coverUrl } from "../../lib/api";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Area, AreaChart } from "recharts";
import { DollarSign, ShoppingBag, Users, BookOpen, ListChecks, Briefcase, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

const Stat = ({ label, value, icon: Icon, hint, testid }) => (
  <div className="border border-border rounded-sm p-5 bg-card relative overflow-hidden" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-muted-foreground">{label}</div>
        <div className="text-3xl font-display tracking-tight mt-3">{value}</div>
        {hint && <div className="text-xs text-muted-foreground mt-1 italic">{hint}</div>}
      </div>
      <div className="h-9 w-9 rounded-sm bg-muted/60 flex items-center justify-center text-accent"><Icon className="h-4 w-4" strokeWidth={1.5} /></div>
    </div>
  </div>
);

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="font-display text-4xl tracking-tight">Dashboard</h1>
        <p className="text-sm text-muted-foreground italic mt-1">A bird&apos;s-eye view of the press.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-10">
        <Stat label="Revenue" value={`A$${data.stats.revenue}`} icon={DollarSign} testid="kpi-revenue" />
        <Stat label="Units sold" value={data.stats.units} icon={ShoppingBag} testid="kpi-units" />
        <Stat label="Customers" value={data.stats.customers} icon={Users} testid="kpi-customers" />
        <Stat label="Live titles" value={data.stats.products_live} icon={BookOpen} testid="kpi-titles" />
        <Stat label="Reviews" value={data.stats.reviews} icon={ListChecks} testid="kpi-reviews" />
        <Stat label="Open tasks" value={data.stats.open_tasks} icon={ListChecks} testid="kpi-tasks" />
        <Stat label="Open deals" value={data.stats.deals_open} icon={Briefcase} testid="kpi-deals" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-border rounded-sm p-6 bg-card">
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="text-sm font-medium">Revenue · last 30 days</div>
              <div className="text-xs text-muted-foreground italic">Across all paid orders.</div>
            </div>
          </div>
          {data.trend.length === 0 ? (
            <div className="h-56 flex items-center justify-center text-sm text-muted-foreground italic">No sales yet. Your trend will appear here.</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={data.trend}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(36 35% 55%)" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="hsl(36 35% 55%)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 2" stroke="hsl(var(--border))" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Area type="monotone" dataKey="revenue" stroke="hsl(36 35% 50%)" strokeWidth={2} fill="url(#rev)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="border border-border rounded-sm p-6 bg-card">
          <div className="text-sm font-medium mb-4">Top titles</div>
          {data.top_titles.length === 0 ? (
            <div className="text-sm text-muted-foreground italic">No data yet.</div>
          ) : (
            <ul className="space-y-3">
              {data.top_titles.map((p) => (
                <li key={p.id} className="flex items-center gap-3">
                  <img src={coverUrl(p)} alt={p.title} className="w-10 h-12 object-cover rounded-sm border border-border/60" />
                  <div className="flex-1 min-w-0">
                    <Link to={`/admin/products`} className="text-sm font-display truncate block hover:text-accent">{p.title}</Link>
                    <div className="text-xs text-muted-foreground">{p.units_sold} sold · A${p.price.toFixed(2)}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-8 border border-border rounded-sm bg-card">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="text-sm font-medium">Recent orders</div>
          <Link to="/admin/orders" className="text-xs text-muted-foreground hover:text-accent inline-flex items-center gap-1">View all <ArrowUpRight className="h-3 w-3" strokeWidth={1.5} /></Link>
        </div>
        {data.recent_orders.length === 0 ? (
          <div className="px-6 py-8 text-sm text-muted-foreground italic">No recent orders.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              <tr><th className="px-6 py-3">Order</th><th>Buyer</th><th>Total</th><th>Date</th></tr>
            </thead>
            <tbody>
              {data.recent_orders.map((o) => (
                <tr key={o.id} className="border-t border-border">
                  <td className="px-6 py-3 font-mono text-xs">{o.id.slice(0, 14)}…</td>
                  <td>{o.buyer_email}</td>
                  <td>{o.currency === "usd" ? "US$" : "A$"}{o.total.toFixed(2)}</td>
                  <td className="text-xs text-muted-foreground">{o.created_at?.slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
